import { Box } from '@mui/material';
import { ReactElement, useEffect, useCallback, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';

import { getUserChatTopicListWithFilter } from '@app/adapter/chat-service';
import { Loading } from '@app/components/Shared/Loading';
import { TopNav } from '@app/components/TopNav';
import {
  TOKEN_VERIFY_STATUS,
  useVerifyStoredAccessToken,
  loggedInUserState,
  userAuthInfoSelector,
} from '@app/domain/app';
import { totalUnreadCountState } from '@app/domain/chat';
import { AppContext } from '@app/hooks/appContext';
import { chatClient } from '@app/socket/chat-socket';
import { isError } from '@app/utils/error';

interface DefaultLayoutProps {
  children: ReactElement;
}
const PROFILE_EDIT_PAGE_PATH = '/profile/edit';
const PROFILE_CONFIRM_PAGE_PATH = '/profile/edit-confirm';

// TODO: To confirm that this works with ionic-route
export function DefaultLayout({ children }: DefaultLayoutProps): ReactElement {
  const authInfo = useRecoilValue(userAuthInfoSelector);
  const userAuthInfoState = useRecoilValue(userAuthInfoSelector);
  const [unreadCount, setUnreadCount] = useRecoilState(totalUnreadCountState);
  useEffect(() => {
    return () => {
      // FIXME: To remove this after we have logout / session management
      // To disconnect the chatClient when component unmounts
      chatClient.disconnect();
    };
  }, []);

  const fetchMessageUnreadCount = useCallback(async () => {
    if (!userAuthInfoState || !authInfo) {
      return;
    }
    try {
      const result = await getUserChatTopicListWithFilter(
        authInfo.accessToken,
        authInfo.fingerPrint,
        userAuthInfoState.userId,
        { expand: 'message', orderBy: 'lastMessageAt desc' }
      );
      const totalUnreadMessage = result.data.value.reduce((count, item) => {
        return count + item.unreadCount;
      }, 0);

      setUnreadCount(totalUnreadMessage);
    } catch (error) {
      if (isError(error)) {
        console.error('fetchMessagesAndUnreadCount', error.message);
      }
    }
  }, [authInfo, setUnreadCount, userAuthInfoState]);

  // ログイン状態検証
  const verifyState = useVerifyStoredAccessToken();
  const loggedInUser = useRecoilValue(loggedInUserState);
  const navigate = useNavigate();
  const location = useLocation();

  // 認証済なのに名前等が未登録の場合はプロフィール登録へ強制遷移
  // (無名のままchatとかできてしまうと混乱しそうなため)
  useEffect(() => {
    if (
      [PROFILE_EDIT_PAGE_PATH, PROFILE_CONFIRM_PAGE_PATH].includes(
        location.pathname
      ) ||
      verifyState !== TOKEN_VERIFY_STATUS.VALID ||
      !loggedInUser
    ) {
      return; // SKIP
    }
    if (
      loggedInUser.id &&
      (!loggedInUser.customFields.familyName ||
        !loggedInUser.customFields.firstName)
    ) {
      navigate(PROFILE_EDIT_PAGE_PATH);
    }
  }, [verifyState, loggedInUser, location, navigate]);

  return verifyState === TOKEN_VERIFY_STATUS.PROGRESS ? (
    <Loading />
  ) : (
    <AppContext.Provider
      value={{
        authInfo,
        fetchMessageUnreadCount,
        setUnreadCount,
        unreadCount,
      }}
    >
      <TopNav />
      <Suspense fallback={<Loading />}>
        <Box component="main">{children}</Box>
      </Suspense>
    </AppContext.Provider>
  );
}
